import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { formatApiErrorDetail } from '../../lib/api';
import { F, Btn } from '../../lib/bricks';
import ProgressReportPaper from './ProgressReportPaper';
import {
  REPORT_EXPIRY, emptyForm, formFromReport, formError, payloadFromForm,
  shareLine, sortReports, pdfName, periodLabel, reportNoLabel, SCHEDULES, MAX_LINES,
  investorFormFromSuggestion, progressFormFromSuggestion, kindName, blankDetails, defaultRange,
  changeFromSummary, changeIsCurrent, detailsPayload, volumeText, areaText, dayLabel,
} from './progressReportModel';
import { MilestonesEditor, LinesEditor, IssuesEditor, CaptureEditor, DETAILS_CSS } from './ProgressReportDetailsForm';
import { sheetState } from './procoreModel';

const MUTED = 'var(--c-ink-mute)';
const LINE = `1px solid var(--c-hair-s)`;
const BAD = 'var(--c-bad)';
const field = { padding: '10px 12px', minHeight: 44, border: '1px solid var(--c-line)', borderRadius: 2, fontSize: 14, minWidth: 0, width: '100%', boxSizing: 'border-box', fontFamily: F.display, background: 'var(--c-field)', color: 'var(--c-ink)' };
const label = { display: 'block', fontSize: 12, color: MUTED, marginBottom: 6 };
const quiet = { minHeight: 44, padding: '0 14px', fontSize: 13 };

const CSS = `${DETAILS_CSS}
.prp-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
.prp-two{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px}
.prp-root,[role="dialog"].prp-root{position:fixed;inset:0;max-width:none !important;max-height:none !important;z-index:2000;overflow-y:auto;background:var(--c-bg);-webkit-overflow-scrolling:touch}
.prp-bar{position:sticky;top:0;z-index:1;background:var(--c-bg);border-bottom:${LINE}}
.prp-in{max-width:1040px;margin:0 auto;padding:0 56px;box-sizing:border-box}
.prp-bar .prp-in{display:flex;flex-wrap:wrap;justify-content:flex-end;align-items:center;gap:8px;min-height:64px}
.prp-share{display:flex;flex-wrap:wrap;gap:8px;align-items:center;padding:14px 56px;max-width:1040px;margin:0 auto;box-sizing:border-box}
.prp-row{display:grid;grid-template-columns:72px minmax(0,1fr) auto;gap:0 16px;align-items:baseline}
@media (max-width:719px){
  .prp-grid{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}
  .prp-grid>.prp-wide{grid-column:1 / -1}
  .prp-two{grid-template-columns:1fr}
  .prp-in{padding:0 20px}
  .prp-share{padding:12px 20px}
  .prp-row{grid-template-columns:56px minmax(0,1fr) auto}
}
`;

// True when a file in the evidence index has no SHA-256 yet.
export function missingChecksums(view) {
  return ((view && view.evidence) || []).some((e) => e.kind !== 'note' && (e.url || e.link) && !e.sha256);
}

function errText(e, fallback) {
  return formatApiErrorDetail(e?.response?.data?.detail) || fallback;
}

async function blobError(e, fallback) {
  try {
    const t = await e.response.data.text();
    return formatApiErrorDetail(JSON.parse(t).detail) || fallback;
  } catch {
    return fallback;
  }
}

// view: the saved report's view (E refs, photos, visits) when editing.
// detailsReady: false until the details column is in the database.
export function ReportForm({ initial, onSave, onCancel, busy, saveLabel = 'Save report', view = null, detailsReady = true, revising = false }) {
  const [f, setF] = useState(initial);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setInv = (k) => (e) => setF({ ...f, investor: { ...(f.investor || {}), [k]: e.target.value } });
  const inv = f.investor || {};
  const investor = f.kind === 'investor';
  const d = f.details || blankDetails();
  const setD = (k) => (v) => setF({ ...f, details: { ...d, [k]: v } });

  function submit(e) {
    e.preventDefault();
    const msg = formError(f, view);
    setErr(msg);
    if (!msg) onSave(payloadFromForm(f, view, { withDetails: detailsReady }));
  }

  return (
    <form onSubmit={submit} data-testid="report-form" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 880, marginTop: 16 }}>
      <div>
        <label style={label} htmlFor="pr-title">Title</label>
        <input id="pr-title" value={f.title} onChange={set('title')} maxLength={120} placeholder={investor ? 'Investor update' : 'Site progress report'} style={field} />
      </div>
      <div className="prp-grid">
        <div><label style={label} htmlFor="pr-from">From</label><input id="pr-from" type="date" value={f.date_from} onChange={set('date_from')} style={field} /></div>
        <div><label style={label} htmlFor="pr-to">To</label><input id="pr-to" type="date" value={f.date_to} onChange={set('date_to')} style={field} /></div>
        <div className="prp-wide"><label style={label} htmlFor="pr-pct">% complete (optional)</label><input id="pr-pct" inputMode="numeric" value={f.percent} onChange={set('percent')} placeholder="e.g. 42" style={field} /></div>
      </div>
      <div className="prp-two">
        <div><label style={label} htmlFor="pr-for">Prepared for (optional)</label><input id="pr-for" value={f.prepared_for || ''} onChange={set('prepared_for')} maxLength={120} placeholder="e.g. Northbank Capital" style={field} /></div>
        <div><label style={label} htmlFor="pr-by">Prepared by (optional)</label><input id="pr-by" value={f.prepared_by || ''} onChange={set('prepared_by')} maxLength={120} placeholder="Your company" style={field} /></div>
      </div>
      {investor ? (
        <div role="group" aria-labelledby="pr-inv-h" data-testid="investor-fields" style={{ borderTop: LINE, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <span id="pr-inv-h" style={{ fontSize: 14, fontWeight: 500 }}>Schedule and budget</span>
          <p style={{ margin: 0, fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}>Your own figures. The update labels them as yours; photos and files come from your dated visits.</p>
          <div className="prp-grid">
            <div className="prp-wide"><label style={label} htmlFor="pr-sched">Schedule</label>
              <select id="pr-sched" value={inv.schedule || ''} onChange={setInv('schedule')} style={field}>
                {SCHEDULES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select></div>
            <div><label style={label} htmlFor="pr-target">Target completion</label><input id="pr-target" type="date" value={inv.target_completion || ''} onChange={setInv('target_completion')} style={field} /></div>
            <div><label style={label} htmlFor="pr-forecast">Forecast completion</label><input id="pr-forecast" type="date" value={inv.forecast_completion || ''} onChange={setInv('forecast_completion')} style={field} /></div>
          </div>
          <div className="prp-two">
            <div><label style={label} htmlFor="pr-budget">Budget ($)</label><input id="pr-budget" inputMode="decimal" value={inv.budget || ''} onChange={setInv('budget')} placeholder="e.g. 4,200,000" style={field} /></div>
            <div><label style={label} htmlFor="pr-spent">Spent to date ($)</label><input id="pr-spent" inputMode="decimal" value={inv.spent_to_date || ''} onChange={setInv('spent_to_date')} placeholder="e.g. 1,640,000" style={field} /></div>
          </div>
          <div className="prp-two">
            <div><label style={label} htmlFor="pr-next">Next steps, one per line (up to {MAX_LINES})</label><textarea id="pr-next" value={inv.next_steps || ''} onChange={setInv('next_steps')} rows={4} style={{ ...field, resize: 'vertical' }} /></div>
            <div><label style={label} htmlFor="pr-risks">Risks, one per line (up to {MAX_LINES})</label><textarea id="pr-risks" value={inv.risks || ''} onChange={setInv('risks')} rows={4} style={{ ...field, resize: 'vertical' }} /></div>
          </div>
        </div>
      ) : null}
      <MilestonesEditor milestones={f.milestones} onChange={(milestones) => setF({ ...f, milestones })} />
      {detailsReady ? (
        <>
          <LinesEditor lines={d.lines} onChange={setD('lines')} view={view} />
          <IssuesEditor issues={d.issues} onChange={setD('issues')} view={view} />
          <CaptureEditor capture={d.capture} onChange={setD('capture')} view={view} />
        </>
      ) : (
        <p data-testid="details-not-yet" style={{ margin: 0, fontSize: 12.5, color: MUTED, lineHeight: 1.5, borderTop: LINE, paddingTop: 16 }}>
          Progress by line, the issues log and capture details switch on after a scheduled update to your account.
        </p>
      )}
      <div>
        <label style={label} htmlFor="pr-summary">Summary (optional)</label>
        <textarea id="pr-summary" value={f.summary} onChange={set('summary')} maxLength={4000} rows={4} placeholder="What changed on site in this period" style={{ ...field, resize: 'vertical' }} />
      </div>
      {revising ? (
        <div>
          <label style={label} htmlFor="pr-revnote">What changed in this revision (optional)</label>
          <input id="pr-revnote" value={f.revision_note || ''} onChange={set('revision_note')} maxLength={160} placeholder="e.g. Added the north wall photos" style={field} />
        </div>
      ) : null}
      {err ? <p role="alert" style={{ color: BAD, fontSize: 13, margin: 0 }}>{err}</p> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn variant="primary" type="submit" disabled={busy} style={{ cursor: busy ? 'wait' : 'pointer' }}>{busy ? 'Saving…' : saveLabel}</Btn>
        <Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
      </div>
    </form>
  );
}

// Full-screen report: download, share with an expiry, edit, delete.
export function ReportSheet({ projectId, reportId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const url = `/dev/projects/${projectId}/reports/${reportId}`;
  const [procore, setProcore] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/dev/projects/${projectId}/procore`).then((r) => { if (live) setProcore(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  async function load() {
    try {
      const res = await api.get(url);
      setData(res.data);
      return res.data;
    } catch (e) {
      setError(errText(e, 'Couldn’t open the report'));
      return null;
    }
  }
  // SHA-256 for the evidence index: a few passes on open, then the report reloads with them.
  const [hashing, setHashing] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      const first = await load();
      if (!live || !first || !missingChecksums(first.view)) return;
      for (let pass = 0; pass < 4 && live; pass += 1) {
        let r;
        try { r = (await api.post(`${url}/checksums`)).data; } catch { break; }
        if (!live) return;
        setHashing(r);
        if (!r || !r.pending) break;
      }
      if (live) { setHashing(null); load(); }
    })();
    return () => { live = false; };
  }, [url]); // eslint-disable-line

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  async function run(what, fn) {
    setBusy(what);
    setError('');
    try { await fn(); } catch (e) { setError(e.response?.data instanceof Blob ? await blobError(e, 'Couldn’t make the PDF') : e.response ? errText(e, 'Something went wrong; nothing was changed.') : ((e && e.message) || 'Something went wrong; nothing was changed.')); }
    setBusy('');
  }

  const download = () => run('pdf', async () => {
    const res = await api.get(`${url}/pdf`, { responseType: 'blob' });
    const href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = href;
    a.download = pdfName(data && data.view);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 4000);
  });

  async function copy(link) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { setCopied(false); }
  }

  const share = () => run('share', async () => {
    const res = await api.post(`${url}/share`, { expires_in_days: Number(days) });
    setData((d) => ({ ...d, report: res.data }));
    onChanged && onChanged();
    if (res.data?.share?.url) copy(res.data.share.url);
  });
  const revoke = () => run('revoke', async () => {
    const res = await api.delete(`${url}/share`);
    setData((d) => ({ ...d, report: res.data }));
    onChanged && onChanged();
  });
  const sendProcore = () => run('procore', async () => {
    const res = await api.post(`${url}/procore`);
    setProcore((p) => ({ ...p, pushes: [res.data.push, ...((p && p.pushes) || [])] }));
  });
  const remove = () => {
    if (!window.confirm(`Delete this ${kindName(data?.report).toLowerCase()}? Its link stops working. Visits and files stay.`)) return;
    run('delete', async () => { await api.delete(url); onChanged && onChanged(); onClose(); });
  };
  const save = (payload) => run('save', async () => {
    const res = await api.patch(url, payload);
    setData(res.data);
    setEditing(false);
    onChanged && onChanged();
  });

  const [measure, setMeasure] = useState('');
  const measureChangeNow = () => run('change', async () => {
    const view = data.view;
    const clouds = view.compare_clouds;
    const urlFor = (did) => ((view.evidence || []).find((e) => e.did === did) || {}).url;
    setMeasure('Downloading the two point clouds…');
    try {
      const [{ parseLAS, maxPoints }, { measureChange, methodLine }] = await Promise.all([import('./site3dModel'), import('./site3dChange')]);
      const bufs = await Promise.all([clouds.from, clouds.to].map(async (c) => {
        const u = urlFor(c.deliverable_id);
        if (!u) throw new Error('One of the point clouds is no longer attached.');
        const r = await fetch(u);
        if (!r.ok) throw new Error('Couldn’t download one of the point clouds.');
        return r.arrayBuffer();
      }));
      setMeasure('Measuring…');
      const coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      const cap = Math.floor(maxPoints(coarse) / 2);
      const [a, b] = bufs.map((x) => parseLAS(x, cap));
      const { summary } = measureChange(a, b);
      const change = changeFromSummary(clouds, summary, methodLine(summary));
      const details = { ...detailsPayload(formFromReport(data.report, view).details, view), change };
      const res = await api.patch(url, { details, revision_note: 'Measured cut and fill' });
      setData(res.data);
      onChanged && onChanged();
    } catch (e) {
      if (e && e.response) throw e;
      throw new Error((e && e.message) || 'Couldn’t measure cut and fill.');
    } finally {
      setMeasure('');
    }
  });

  const s = data?.report?.share;
  const live = s && s.status === 'active' && s.url;
  const clouds = data?.view?.compare_clouds;
  const savedChange = data?.report?.details?.change;
  const changeCurrent = changeIsCurrent(savedChange, clouds);
  return createPortal(
    <div className="prp-root" data-report-surface="" role="dialog" aria-modal="true" aria-label={kindName(data?.view || data?.report)} data-testid="report-sheet">
      <style>{CSS}</style>
      <div className="prp-bar"><div className="prp-in">
        <button type="button" onClick={onClose} style={{ marginRight: 'auto', minHeight: 44, padding: 0, background: 'none', border: 'none', fontFamily: F.display, fontSize: 13, color: 'var(--c-ink)', cursor: 'pointer' }}>Close</button>
        {data && !editing ? (
          <>
            <Btn variant="ghost" style={quiet} onClick={() => setEditing(true)}>Edit</Btn>
            <Btn variant="accent" style={quiet} onClick={download} disabled={busy === 'pdf'} data-testid="report-download">{busy === 'pdf' ? 'Making PDF…' : 'Download PDF'}</Btn>
          </>
        ) : null}
      </div></div>
      {data && !editing ? (
        <div style={{ borderBottom: LINE }}><div className="prp-share" data-testid="report-share">
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: 14 }}>Read-only link</div>
            <div style={{ fontSize: 12.5, color: MUTED }} data-testid="report-share-line">{shareLine(s)}</div>
          </div>
          {live ? (
            <>
              <Btn variant="ghost" style={quiet} onClick={() => copy(s.url)}>{copied ? 'Copied' : 'Copy link'}</Btn>
              <Btn variant="ghost" style={quiet} onClick={revoke} disabled={busy === 'revoke'}>Turn off</Btn>
            </>
          ) : null}
          <select value={days} onChange={(e) => setDays(e.target.value)} aria-label="Link expires after" style={{ ...field, width: 'auto', minHeight: 44 }}>
            {REPORT_EXPIRY.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
          </select>
          <Btn variant="primary" style={quiet} onClick={share} disabled={busy === 'share'}>{busy === 'share' ? 'Making link…' : live ? 'New link' : 'Share link'}</Btn>
        </div></div>
      ) : null}
      {data && !editing && sheetState(procore, reportId) ? (() => {
        const ps = sheetState(procore, reportId);
        return (
          <div style={{ borderBottom: LINE }}><div className="prp-share" data-testid="report-procore">
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 14 }}>Procore</div>
              <div style={{ fontSize: 12.5, color: MUTED, overflowWrap: 'anywhere' }} data-testid="report-procore-line">{ps.line}</div>
            </div>
            {ps.canSend ? <Btn variant="ghost" style={quiet} onClick={sendProcore} disabled={busy === 'procore'} data-testid="report-procore-send">{busy === 'procore' ? 'Sending…' : ps.sent ? 'Send again' : 'Send to Procore'}</Btn> : null}
          </div></div>
        );
      })() : null}
      {data && !editing && clouds && data.details_ready !== false ? (
        <div style={{ borderBottom: LINE }}><div className="prp-share" data-testid="report-change">
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: 14 }}>Cut and fill</div>
            <div style={{ fontSize: 12.5, color: MUTED }} data-testid="report-change-line">
              {measure || (changeCurrent
                ? `Fill ${volumeText(savedChange.fill_m3)}, cut ${volumeText(savedChange.cut_m3)} over ${areaText(savedChange.changed_area_m2)}. Measured ${dayLabel(savedChange.measured_on)}.`
                : `Compare ${clouds.from.ref} (visit ${clouds.from.visit_no}) with ${clouds.to.ref} (visit ${clouds.to.visit_no}) in your browser. Takes a minute on large files.`)}
            </div>
          </div>
          <Btn variant="ghost" style={quiet} onClick={measureChangeNow} disabled={busy === 'change'} data-testid="report-change-measure">{busy === 'change' ? 'Measuring…' : changeCurrent ? 'Measure again' : 'Measure cut and fill'}</Btn>
        </div></div>
      ) : null}
      {data && !editing && hashing && hashing.pending ? (
        <p className="prp-in" data-testid="report-hashing" style={{ margin: '12px auto 0', color: MUTED, fontSize: 12.5 }}>
          {`Adding SHA-256 checksums to the evidence index: ${hashing.done} of ${hashing.total} files.`}
        </p>
      ) : null}
      {error ? <p role="alert" className="prp-in" style={{ margin: '12px auto 0', color: BAD, fontSize: 13 }}>{error}</p> : null}
      <div>
        {editing && data ? (
          <div className="prp-in" style={{ paddingTop: 24, paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}>
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 300, letterSpacing: '-0.02em' }}>{data.report?.kind === 'investor' ? 'Edit update' : 'Edit report'}</h2>
            <ReportForm initial={formFromReport(data.report, data.view)} view={data.view} detailsReady={data.details_ready !== false} revising
              busy={busy === 'save'} onSave={save} onCancel={() => setEditing(false)} saveLabel="Save changes" />
            <div style={{ borderTop: LINE, marginTop: 24, paddingTop: 16 }}>
              <Btn variant="ghost" style={{ ...quiet, color: BAD }} onClick={remove}>{data.report?.kind === 'investor' ? 'Delete update' : 'Delete report'}</Btn>
            </div>
          </div>
        ) : data ? <ProgressReportPaper view={data.view} /> : (
          !error ? <p style={{ color: MUTED, textAlign: 'center', padding: 48, fontSize: 13 }}>Loading report…</p> : null
        )}
      </div>
    </div>,
    document.body,
  );
}

const COPY = {
  progress: {
    heading: 'Progress reports',
    blurb: 'For your lender or investors: every visit in a date range, before and after, your % complete and milestones, and an evidence index. Download a PDF or share a read-only link that expires.',
    add: 'New report', none: 'No reports yet.', fallback: 'Site progress report',
  },
  investor: {
    heading: 'Investor updates',
    blurb: 'A monthly update for your investors: what changed since the last one, your schedule and budget, next steps and risks, with the month’s visits and files. Download a PDF or share a link that expires.',
    add: 'New monthly update', none: 'No investor updates yet.', fallback: 'Investor update',
  },
};

// Builder: lender/investor reports over a date range for one project.
export default function ProgressReportsPanel({ projectId, timeline, kind = 'progress' }) {
  const [reports, setReports] = useState(null);
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(true);
  const copy = COPY[kind] || COPY.progress;
  const investor = kind === 'investor';

  async function load() {
    try {
      const res = await api.get(`/dev/projects/${projectId}/reports`, { params: { kind } });
      setReports(((res.data && res.data.reports) || []).filter((r) => (r.kind || 'progress') === kind));
      setError('');
    } catch (e) {
      setReports([]);
      setError(errText(e, 'Couldn’t load reports'));
    }
  }
  useEffect(() => { load(); }, [projectId, kind]); // eslint-disable-line

  const [detailsReady, setDetailsReady] = useState(true);
  async function startNew() {
    setError('');
    if (!investor) {
      try {
        const res = await api.get(`/dev/projects/${projectId}/reports/suggest`, { params: { kind: 'progress' } });
        setDetailsReady(!(res.data && res.data.details_ready === false));
        const s = res.data && res.data.suggestion;
        if (s) {
          const f = progressFormFromSuggestion(s);
          // A first report spans the visits so far, like a blank one; later ones pick up after the last.
          setCreating(s.carried_from ? f : { ...f, ...defaultRange(timeline), title: '' });
          return;
        }
      } catch { /* a blank form still works */ }
      setCreating(emptyForm(timeline));
      return;
    }
    try {
      const res = await api.get(`/dev/projects/${projectId}/reports/suggest`);
      if (res.data && res.data.available === false) { setAvailable(false); return; }
      setDetailsReady(!(res.data && res.data.details_ready === false));
      setCreating(investorFormFromSuggestion(res.data && res.data.suggestion));
    } catch (e) {
      setError(errText(e, 'Couldn’t start an update right now.'));
    }
  }

  async function create(payload) {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/dev/projects/${projectId}/reports`, payload);
      setCreating(null);
      await load();
      if (res.data?.report?.id) setOpen(res.data.report.id);
    } catch (e) {
      setError(errText(e, 'Couldn’t save the report; nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: LINE, paddingTop: 16, margin: '4px 0 20px' }} data-testid={investor ? 'investor-updates' : 'progress-reports'}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{copy.heading}</h3>
        {!creating && available ? <Btn variant="ghost" style={quiet} onClick={startNew} data-testid={`new-${kind}`}>{copy.add}</Btn> : null}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: MUTED, margin: '6px 0 12px', maxWidth: 560 }}>{copy.blurb}</p>
      {!available ? <p data-testid="investor-not-yet" style={{ fontSize: 13, color: MUTED, margin: '0 0 12px' }}>Investor updates switch on after a scheduled update to your account. Progress reports work now.</p> : null}
      {creating ? <ReportForm key={creating.kind + creating.date_to} initial={creating} detailsReady={detailsReady} busy={busy} onSave={create} onCancel={() => setCreating(null)} saveLabel={investor ? 'Save update' : 'Save report'} /> : null}
      {reports && reports.length ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', maxWidth: 720, borderBottom: LINE }}>
          {sortReports(reports).map((r) => (
            <li key={r.id} style={{ borderTop: LINE }}>
              <button type="button" onClick={() => setOpen(r.id)} data-testid="report-row" className="prp-row"
                style={{ width: '100%', padding: '14px 0', minHeight: 56, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: F.display, color: 'var(--c-ink)' }}>
                <span style={{ fontFamily: F.mono, fontSize: 12.5 }}>{reportNoLabel(r)}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title || copy.fallback}</span>
                  <span style={{ display: 'block', fontFamily: F.mono, fontSize: 11.5, color: MUTED, marginTop: 3 }}>{periodLabel(r.date_from, r.date_to)}{r.percent_complete != null ? ` · ${r.percent_complete}%` : ''}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2 }}>{shareLine(r.share)}</span>
                </span>
                <span style={{ fontSize: 13, color: 'var(--c-ink)' }}>Open</span>
              </button>
            </li>
          ))}
        </ul>
      ) : reports && !creating && !error ? <p style={{ fontSize: 13, color: MUTED, margin: '6px 0 0' }}>{copy.none}</p> : null}
      {error ? <p role="alert" style={{ color: BAD, fontSize: 12.5, margin: '8px 0 0' }}>{error}</p> : null}
      {open ? <ReportSheet projectId={projectId} reportId={open} onClose={() => setOpen(null)} onChanged={load} /> : null}
    </div>
  );
}
